import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import ResourceDataView from './ResourceDataView';
import type { ColumnDefinition } from './ResourceDataView';
export type { ColumnDefinition } from './ResourceDataView';
import {
  Box,
  CircularProgress,
  Paper,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';

type FetchResult<T> = {
  total: number;
  data: T[];
};

interface ResourceListProps<T extends { _id: string | number }> {
  fetchData: (params: {
    page: number;
    limit: number;
    search?: string;
  }) => Promise<FetchResult<T>>;
  columns: ColumnDefinition<T>[];
  resourceName: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  headerActions?: ReactNode;
  defaultRowsPerPage?: number;
  pageSizeOptions?: number[];
}

export default function ResourceList<T extends { _id: string | number }>({
  fetchData,
  columns,
  resourceName,
  searchPlaceholder,
  emptyMessage,
  headerActions,
  defaultRowsPerPage = 10,
  pageSizeOptions = [5, 10, 25, 50, 100],
}: ResourceListProps<T>) {

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [totalItems, setTotalItems] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setSearchTerm(inputValue.trim());
      setPage(0);
    }, 400);

    return () => window.clearTimeout(handler);
  }, [inputValue]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const response = await fetchData({
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm || undefined,
        });

        if (!mounted) {
          return;
        }

        setItems(response.data);
        setTotalItems(response.total);
      } catch (error) {
        console.error(error);
        if (mounted) {
          setItems([]);
          setTotalItems(0);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [fetchData, page, rowsPerPage, searchTerm]);

  const pageCount = useMemo(
    () => Math.max(0, Math.ceil(totalItems / rowsPerPage) - 1),
    [totalItems, rowsPerPage]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" component="h1" fontWeight={800}>
            {resourceName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalItems} item(ns)
          </Typography>
        </Box>
        {headerActions ? <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>{headerActions}</Box> : null}
      </Box>

      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label={searchPlaceholder || `Buscar em ${resourceName.toLowerCase()}...`}
          variant="outlined"
          size="small"
          fullWidth
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
        />
      </Paper>

      <Box sx={{ flexGrow: 1 }}>
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary">
            {emptyMessage || `Nenhum item encontrado em ${resourceName.toLowerCase()}`}
          </Typography>
        ) : (
          <ResourceDataView items={items} columns={columns} resourceName={resourceName} />
        )}
      </Box>

      <TablePagination
        component="div"
        rowsPerPageOptions={pageSizeOptions}
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={Math.min(page, pageCount)}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage="Itens por página:"
        sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: 'flex-end', pl: 0 }, '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } }, '& .MuiTablePagination-selectLabel': { mr: 1 }, '& .MuiTablePagination-displayedRows': { ml: 1 } }}
      />
    </Box>
  );
}
